import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { TopicService } from "@/shared/domain/topics/TopicService";

/**
 * The distinct field paths of a single topic, resolved by name through the
 * shared-topics/fields schema. Disabled until both an org and a non-empty
 * `topicName` are available, so callers can pass `null` while the user hasn't
 * settled on a topic yet. Cached per `(orgId, topicName, contains)`.
 *
 * `contains` narrows the fetch to paths containing it, case-insensitively (see
 * {@link TopicService.getTopicFields}). Pass a debounced value: each distinct
 * `contains` is its own request, so an undebounced one sends a request per
 * keystroke.
 *
 * The previous fetch's paths stand in while the next is in flight, but only
 * within one topic of one org: carried across a change of either, they would
 * show fields of a topic the caller no longer asks about.
 */
export default function useTopicFields(
  topicName: string | null,
  orgId?: string,
  contains?: string,
) {
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;
  const { topics: topicService } = useDomainServices();

  return useQuery<string[], Error>({
    ...topicFieldsQuery(
      topicService,
      canonicalOrgId ?? "",
      topicName ?? "",
      contains,
    ),
    enabled: canonicalOrgId !== undefined && !!topicName,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === canonicalOrgId &&
      previousQuery?.queryKey[2] === topicName
        ? previousData
        : undefined,
  });
}

/**
 * The query `useTopicFields` runs for the field paths of the topic
 * `topicName` in `orgId`, for a caller that lists several topics' fields or
 * fetches outside React (`queryClient.fetchQuery`) through the same cache
 * entry. `contains` narrows it as on `useTopicFields`; omitted or empty, it
 * shares the unfiltered entry.
 */
export function topicFieldsQuery(
  topicService: TopicService,
  orgId: string,
  topicName: string,
  contains?: string,
) {
  return {
    queryKey: ["topicFields", orgId, topicName, contains ?? ""],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      topicService.getTopicFields(topicName, {
        abortSignal: signal,
        resourceOwnerId: orgId,
        contains,
      }),
    staleTime: 30_000,
  };
}
